<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  description,
  image,
  items,
  hasTitle,
  hasDescription,
  hasImage,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
      backgroundColor === 'black' ? 'white' : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="about-manufacturer">
        <div class="about-manufacturer__top max-w-784">
          <NuxtImg
            v-if="hasImage"
            :src="image.src"
            :alt="image.alt"
            :width="image.width"
            :height="image.height"
            class="about-manufacturer__image"
            data-aos="zoom-in"
            data-aos-easing="ease-out-quad"
            loading="lazy"
          />
          <div
            v-if="hasTitle || hasDescription || hasItems"
            class="about-manufacturer__content"
          >
            <h2
              v-if="hasTitle"
              class="mb-0 about-manufacturer__title h2"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="title"
            ></h2>
            <div
              v-if="hasDescription"
              class="about-manufacturer__text body-1-regular"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="description"
            ></div>
          </div>
        </div>
        <ul v-if="hasItems" class="m-0 p-0 about-manufacturer__items">
          <li
            v-for="(item, i) in items"
            :key="i"
            :class="[
              'about-manufacturer__item',
              backgroundColor
                ? `about-manufacturer__item--${backgroundColor}`
                : '',
            ]"
            data-aos="fade-up"
            :data-aos-delay="i >= 1 ? 100 + 100 * i : 100"
          >
            <p
              class="about-manufacturer__item-title m-0 h6-mobile"
              v-html="item.title"
            ></p>
            <div
              class="about-manufacturer__item-description body-2-regular"
              v-html="item.description"
            ></div>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.about-manufacturer {
  display: flex;
  flex-direction: column;
  gap: 56px;
  margin: 0 auto;
  width: 100%;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin: 0 auto;
    width: 100%;
    @include media-breakpoint-up(sm) {
      gap: 64px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 64);
    }
  }
  &__title {
    margin: 0;
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__image {
    border-radius: 4px;
    margin: 0 auto;
    height: auto;
    width: 45%;
    @include media-breakpoint-up(sm) {
      width: 23%;
    }
    @include media-breakpoint-up(md) {
      width: 21.5%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
    }
  }
  &__text {
    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      max-width: 68%;
    }
  }
  &__items {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 0;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(3, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__item {
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;
    margin: 0;
    padding: 24px 24px 30px;
    text-align: left;
    @include media-breakpoint-up(sm) {
      padding-bottom: 28px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(padding, 24);
      @include responsive-spacing(padding-bottom, 28);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
    &-description {
      &:deep(p:last-child) {
        margin: 0;
      }
    }
  }
}
</style>
